import React from "react";
import Carousel from "components/Carousel";
import { useBanners, useDispatch, useSelector } from "hooks";
import { navigate } from "utilities/navigate";
import {
  BANNER_DISPLAY_SECONDS,
  useContentfulSetting,
} from "hooks/graphql/useContentfulSettings";
import { BANNER } from "appConfig";
import { Skeleton } from "components/Skeleton";
import {
  type Card,
  type ImageOnlyCard,
  logContentCardClick,
  logContentCardImpression,
} from "store/Braze";
import { getRelativeUrl } from "utilities/helpers";
import { useContentfulBanners } from "hooks/useContentfulBanners";
import { useIsClient } from "usehooks-ts";
import { useHelpcenterAttributes } from "hooks/firestore/v2/user/useZendesk";

import * as styles from "./Banners.module.scss";

const preloadImages = (banners: ImageOnlyCard[]) => {
  banners.forEach((banner) => {
    const img = new Image();
    img.src = banner.imageUrl || "";
  });
};

export const Banners = () => {
  const dispatch = useDispatch();
  const [preloaded, setPreloaded] = React.useState(false);
  const initialized = useSelector(
    (state) => state.brazeContentCards.initialized,
  );
  const isClient = useIsClient();
  const banners = useBanners();
  const contentfulBanners = useContentfulBanners();
  const contentfulDisplaySeconds = useContentfulSetting(BANNER_DISPLAY_SECONDS);
  const { isZendeskUrl, getHelpcenterAttributes } = useHelpcenterAttributes();
  const displaySeconds =
    parseInt(contentfulDisplaySeconds) || BANNER.displaySeconds;

  const handleClick = (url: string | undefined, card: Card) => {
    dispatch(logContentCardClick(card));

    if (!url || isZendeskUrl(url)) return;

    const isRelativeUrl = url.startsWith("/") || url.includes("picklebet.com");

    if (isRelativeUrl) {
      navigate(getRelativeUrl(url));
    } else {
      window.open(url, "_blank");
    }
  };

  const handleLoaded = (card: Card) => {
    // Preload images after the first image is loaded
    if (!preloaded) {
      preloadImages(banners);
      setPreloaded(true);
    }
    dispatch(logContentCardImpression(card));
  };

  // fall back to contentful banners
  const displayBanners = banners.length ? banners : contentfulBanners;

  if (!isClient) {
    return <Skeleton className={styles.banners} />;
  }

  if (!displayBanners.length) {
    if (initialized) {
      // initialised, but we have no cached banners and nothing from braze or contentful - there is nothing to show
      return null;
    }
    return <Skeleton className={styles.banners} />;
  }

  return (
    <div className={styles.banners}>
      <Carousel.Root displaySeconds={displaySeconds}>
        {displayBanners.map((banner) => {
          const { url, imageUrl, linkText, extras } = banner;

          const desktopImageUrl = extras?.desktopImageUrl;
          const duration = extras?.duration
            ? Number(extras.duration)
            : undefined;

          return (
            <Carousel.Item
              key={url}
              duration={!isNaN(Number(duration)) ? duration : displaySeconds}
              onClick={() => handleClick(url, banner)}
              {...getHelpcenterAttributes(url)}
            >
              <picture onLoad={() => handleLoaded(banner)}>
                <source
                  media="(max-width:768px)"
                  srcSet={imageUrl}
                  title={linkText}
                />
                <img
                  src={desktopImageUrl ?? imageUrl}
                  alt={linkText}
                  title={linkText}
                />
              </picture>
            </Carousel.Item>
          );
        })}
      </Carousel.Root>
    </div>
  );
};
